import { Clock, Download, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useInstructionVersions, useUploadVersion } from "../hooks/useInstructions";

interface Props {
  instructionId: string;
  canUpload: boolean;
}

export function VersionHistoryPanel({ instructionId, canUpload }: Props) {
  const { data: versions = [], isLoading } = useInstructionVersions(instructionId);
  const uploadMutation = useUploadVersion();
  const [comment, setComment] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadMutation.mutateAsync({ id: instructionId, file, comment });
    setComment("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        <Clock size={15} />
        Historia wersji
      </div>

      {canUpload && (
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 space-y-2">
          <input
            type="text"
            placeholder="Opis zmian (opcjonalnie)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline">
            <Upload size={14} />
            {uploadMutation.isPending ? "Przesyłanie..." : "Dodaj nową wersję PDF"}
            <input
              ref={fileRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleUpload}
              disabled={uploadMutation.isPending}
            />
          </label>
          {uploadMutation.isError && (
            <p className="text-xs text-red-500">Błąd przesyłania pliku.</p>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400">Ładowanie...</p>
      ) : versions.length === 0 ? (
        <p className="text-sm text-gray-400 italic">Brak wersji.</p>
      ) : (
        <ul className="space-y-1.5">
          {versions.map((v) => (
            <li
              key={v.id}
              className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2"
            >
              <div className="min-w-0 overflow-hidden">
                <div className="flex items-baseline gap-1 min-w-0">
                  <span className="font-medium text-gray-700 dark:text-gray-200 flex-shrink-0">
                    v{v.version_number}
                  </span>
                  {v.change_comment && (
                    <span className="text-gray-500 dark:text-gray-400 truncate min-w-0">
                      — {v.change_comment}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-0.5 truncate">
                  {v.uploaded_by_name} · {new Date(v.created_at).toLocaleDateString("pl-PL")}
                </div>
              </div>
              {v.file_url && (
                <a
                  href={v.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-3 p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0"
                  title="Pobierz"
                >
                  <Download size={14} />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
